/**
 * O avatar do topo, com o que aconteceu enquanto você não estava olhando.
 *
 * <p>Fica no canto superior direito, fora da barra lateral: a lateral é para onde ir, e
 * isto é o que veio até você. São perguntas diferentes, e misturá-las faria o aviso
 * competir com a navegação.
 *
 * <p>A "minha página" entra aqui na fatia seguinte — por ora o menu mostra as três mais
 * recentes e deixa marcar tudo como lido.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { api } from '../api/cliente';
import { useSessao } from '../auth/SessaoContext';
export { iniciais } from '../comum/iniciais';
import { iniciais } from '../comum/iniciais';
import { Avatar } from '../comum/Avatar';
import { useImagemAutenticada } from '../comum/ImagemAutenticada';
import { tempoRelativoCurto } from '../comum/tempoRelativo';
import './notificacoes.css';

export interface Notificacao {
  id: number;
  tipo: 'TAREFA_ATRIBUIDA' | 'SUBTAREFA_ATRIBUIDA' | 'MENCAO' | 'COMENTARIO_NA_TAREFA';
  autorId: number | null;
  autorNome: string | null;
  autorCor: string | null;
  /** A foto de quem causou, ou nulo em quem não subiu nenhuma. */
  autorAvatar: string | null;
  /*
   * Opcionais, e não `| null`: a API serializa sem os campos nulos
   * (`default-property-inclusion: non_null`), então num aviso de seção a frente
   * simplesmente não vem — quem testar `'tarefaId' in n` erraria.
   */
  /** Ausentes quando o aviso é de uma seção do GDD. */
  tarefaId?: number;
  tarefaNome?: string;
  /** Card do Dev Board, e não frente do roadmap — é o que decide para onde o clique vai. */
  doQuadro: boolean;
  /** A âncora da seção do GDD (`s12`), quando a citação foi na revisão do documento. */
  secaoChave?: string;
  /** "12 Encontros selvagens" — o rótulo que a linha mostra. */
  secaoTitulo?: string;
  comentarioId: number | null;
  trecho: string | null;
  itemTexto: string | null;
  criadoEm: string;
  lida: boolean;
}

export const CHAVE_NOTIFICACOES = ['notificacoes'] as const;
export const CHAVE_CONTAGEM = ['notificacoes', 'contagem'] as const;

/** Quantas o menu mostra. A lista inteira é assunto da página do usuário. */
const NO_MENU = 3;

/**
 * A frase, montada aqui e não no servidor.
 *
 * <p>A mesma notificação vira uma linha curta no menu e uma linha completa na página; o
 * servidor manda as partes e cada tela escreve a sua. Gravar a frase pronta congelaria o
 * nome da frente no dia do evento.
 *
 * <p>{@code euId} existe por causa da atribuição a si mesmo, que passou a notificar: sem
 * ele a lista diria "Theo atribuiu a você" para o próprio Theo, como se outra pessoa
 * tivesse feito.
 */
export function fraseDe(n: Notificacao, euId?: number): { acao: string; alvo: string } {
  const quem = n.autorNome ?? 'Alguém';
  const fuiEu = euId != null && n.autorId === euId;
  /*
   * O alvo é a seção quando o aviso vem da revisão do GDD, e a frente no resto. Um só
   * lugar decide isso: escrever `n.tarefaNome` em cada ramo faria a linha do GDD
   * aparecer vazia — a frente é nula ali.
   */
  const alvo = n.secaoTitulo ?? n.tarefaNome ?? 'algo que não existe mais';
  switch (n.tipo) {
    case 'TAREFA_ATRIBUIDA':
      return { acao: fuiEu ? 'Você assumiu' : `${quem} atribuiu a você`, alvo };
    case 'SUBTAREFA_ATRIBUIDA':
      return {
        acao: fuiEu ? 'Você assumiu a sub-tarefa' : `${quem} atribuiu a você a sub-tarefa`,
        alvo: n.itemTexto ?? alvo,
      };
    case 'MENCAO':
      // "no GDD" porque a seção sozinha ("13 Atributos") não diz onde ela está.
      return { acao: `${quem} citou você ${n.secaoChave ? 'no GDD, em' : 'em'}`, alvo };
    default:
      return { acao: `${quem} comentou em`, alvo };
  }
}

/**
 * Para onde o clique numa notificação leva.
 *
 * <p>Card do Dev Board e frente do roadmap são a mesma tabela desde a V32, então o id
 * sozinho não diz em que tela a coisa aparece. Isto mandava tudo para
 * {@code /roadmap?frente=<id>} — e o roadmap, que filtra {@code coluna is null}, não
 * achava o card: a tela abria e nada acontecia, sem erro nenhum para explicar por quê.
 *
 * <p>Exportada porque a página do usuário lista as mesmas notificações e precisa levar
 * aos mesmos lugares. Duas cópias desta escolha divergiriam na primeira tela nova.
 */
export function rotaDaNotificacao(n: Notificacao): string {
  // A citação numa seção do GDD leva ao documento, na âncora da seção — a mesma que o
  // índice da página usa.
  if (n.secaoChave) return `/game-design/gdd#${n.secaoChave}`;
  return n.doQuadro ? `/dev-board?card=${n.tarefaId}` : `/roadmap?frente=${n.tarefaId}`;
}


export function AvatarComNotificacoes() {
  const { usuario, sair } = useSessao();
  const navegar = useNavigate();
  const fila = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  /*
   * Aqui o avatar é o próprio botão, então não dá para usar o `<Avatar>` — ele desenha um
   * `<span>`, e um botão dentro de um span perderia o clique. A regra é a mesma; o que
   * muda é onde a foto é aplicada. O hook fica antes do `if (!usuario)` porque hook não
   * pode ficar depois de um retorno.
   */
  const foto = useImagemAutenticada(usuario?.avatar || null);

  /**
   * Só o número, e de tempos em tempos.
   *
   * <p>É o que a bolinha precisa. Baixar a lista inteira a cada trinta segundos para
   * desenhar um "3" seria pagar caro por um dígito — a lista vem quando o menu abre.
   */
  const { data: contagem } = useQuery<{ naoLidas: number }>({
    queryKey: CHAVE_CONTAGEM,
    queryFn: () => api('/api/notificacoes/contagem'),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });

  const { data: recentes } = useQuery<Notificacao[]>({
    queryKey: CHAVE_NOTIFICACOES,
    queryFn: () => api(`/api/notificacoes?limite=${NO_MENU}`),
    // Só busca quando o menu está aberto: fechado, ninguém vê a lista.
    enabled: aberto,
  });

  const marcarTodas = useMutation({
    mutationFn: () => api<void>('/api/notificacoes/lidas', { method: 'POST' }),
    onSuccess: () => {
      void fila.invalidateQueries({ queryKey: CHAVE_CONTAGEM });
      void fila.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES });
    },
  });

  const marcarUma = useMutation({
    mutationFn: (id: number) => api<void>(`/api/notificacoes/${id}/lida`, { method: 'POST' }),
    onSuccess: () => {
      void fila.invalidateQueries({ queryKey: CHAVE_CONTAGEM });
      void fila.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES });
    },
  });

  // Clicar fora fecha. Sem isto o menu fica aberto atrás do resto da tela.
  useEffect(() => {
    if (!aberto) return;
    function aoClicar(e: MouseEvent) {
      if (!caixa.current?.contains(e.target as Node)) setAberto(false);
    }
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') setAberto(false);
    }
    document.addEventListener('mousedown', aoClicar);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicar);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  if (!usuario) return null;

  const naoLidas = contagem?.naoLidas ?? 0;
  const lista = recentes ?? [];

  function abrir(n: Notificacao) {
    if (!n.lida) marcarUma.mutate(n.id);
    setAberto(false);
    navegar(rotaDaNotificacao(n));
  }

  return (
    <div className="nt" ref={caixa}>
      <button
        className={`nt-avatar av-comum${aberto ? ' on' : ''}${foto ? ' com-foto' : ''}`}
        style={
          {
            '--c': usuario.cor,
            ...(foto ? { backgroundImage: `url('${foto}')` } : null),
          } as React.CSSProperties
        }
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-label={
          naoLidas > 0 ? `${naoLidas} notificações não lidas` : 'Notificações'
        }
        title={usuario.nome}
      >
        <span className="av-iniciais">{iniciais(usuario.nome)}</span>
        {naoLidas > 0 && (
          <span className="nt-bolinha" aria-hidden="true">
            {naoLidas > 99 ? '99+' : naoLidas}
          </span>
        )}
      </button>

      {aberto && (
        <div className="nt-menu" role="menu">
          <div className="nt-cabeca">
            <b>Notificações</b>
            {naoLidas > 0 && (
              <button className="nt-limpar" onClick={() => marcarTodas.mutate()}>
                marcar todas como lidas
              </button>
            )}
          </div>

          {lista.length === 0 ? (
            <div className="nt-vazio">
              Nada por aqui.
              <br />
              Você é avisado quando recebe uma frente, é citado num comentário, ou alguém
              comenta numa frente sua.
            </div>
          ) : (
            lista.map((n) => {
              const { acao, alvo } = fraseDe(n, usuario?.id);
              return (
                <button
                  key={n.id}
                  className={`nt-item${n.lida ? '' : ' nova'}`}
                  role="menuitem"
                  onClick={() => abrir(n)}
                >
                  <Avatar
                    className="nt-av"
                    nome={n.autorNome ?? '—'}
                    cor={n.autorCor}
                    url={n.autorAvatar}
                  />
                  <span className="nt-corpo">
                    <span className="nt-frase">
                      {acao} <b>{alvo}</b>
                    </span>
                    {n.trecho && <span className="nt-trecho">{n.trecho}</span>}
                    <span className="nt-quando">{tempoRelativoCurto(n.criadoEm)}</span>
                  </span>
                  {!n.lida && <span className="nt-ponto" aria-hidden="true" />}
                </button>
              );
            })
          )}

          {/* "Ver todas" só aparece quando há mais do que cabe no menu — senão ele
              prometeria uma página com o mesmo que já está aqui. */}
          {lista.length >= NO_MENU && (
            <button
              className="nt-todas"
              onClick={() => {
                setAberto(false);
                navegar('/eu?aba=notificacoes');
              }}
            >
              ver todas
            </button>
          )}

          <div className="nt-rodape">
            <button
              className="nt-eu"
              onClick={() => {
                setAberto(false);
                navegar('/eu');
              }}
            >
              <Avatar
                className="nt-eu-av"
                nome={usuario.nome}
                cor={usuario.cor}
                url={usuario.avatar || null}
              />
              <span className="nt-eu-txt">
                <b>{usuario.nome}</b>
                <span>Minha página</span>
              </span>
            </button>
            <button className="nt-sair" onClick={() => void sair()}>
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
